import { useState } from 'react';

import { Card, CardHeader } from '../components/ui/Card';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../components/ui/AsyncBoundary';
import { Modal, useToast } from '../components/ui/Overlay';
import { Button } from '../components/ui/Button';
import { Field, Input, Select } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { OrgField, useOrgField } from '../components/domain/OrgField';
import { useResource, useMutation } from '../hooks/useResource';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';

/**
 * The rules that raise alerts.
 *
 * Thresholds were configuration living in the database with no way to reach
 * them, which meant every organisation ran on whatever the seed happened to
 * write. A cold-chain operator and a haulage operator do not agree on what a
 * high temperature is, and neither should have to ask an engineer.
 *
 * The metric list comes from the server rather than being hard-coded here — it
 * is the same list the rule evaluator checks against, so a metric can never be
 * offered that the evaluator would ignore.
 */

const OPERATORS = [
  { value: '>', label: 'is above' },
  { value: '>=', label: 'is at or above' },
  { value: '<', label: 'is below' },
  { value: '<=', label: 'is at or below' },
  { value: '==', label: 'equals' },
  { value: '!=', label: 'is not' },
];

const METRIC_UNITS = {
  speed: 'km/h',
  temperature: '°C',
  humidity: '%',
  memory: '%',
  gsm_signal: 'asu',
  satellites: 'count',
  hdop: '',
  tilt: '°',
  health: 'score',
};

function metricLabel(metric) {
  return metric.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export function AlertRulesPanel() {
  const toast = useToast();
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const rules = useResource('/api/alerts/rules');

  const toggle = async (rule) => {
    setBusyId(rule.id);
    try {
      await api.patch(`/api/alerts/rules/${rule.id}`, { enabled: !rule.enabled });
      toast.success(rule.enabled ? `${rule.name} paused.` : `${rule.name} is live.`);
      rules.refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader
          title="Alert rules"
          subtitle="What counts as a problem here — thresholds are per organisation"
          icon="shield"
          actions={
            can('alert:rules') && (
              <Button variant="accent" size="sm" icon="plus" onClick={() => setCreating(true)}>
                New rule
              </Button>
            )
          }
        />

        <AsyncBoundary
          state={rules}
          skeleton={<SkeletonRows rows={4} />}
          isEmpty={(data) => !data.rules.length}
          empty={
            <EmptyState
              icon="shield"
              title="No rules yet"
              description="Without a rule, only the built-in device-health and tamper alerts are raised."
            />
          }
        >
          {(data) => (
            <ul className="divide-y divide-line">
              {data.rules.map((rule) => (
                <li key={rule.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-medium text-ink">{rule.name}</p>
                    <p className="truncate text-[12.5px] text-ink-3">
                      {metricLabel(rule.metric)}{' '}
                      {OPERATORS.find((o) => o.value === rule.operator)?.label || rule.operator}{' '}
                      <span className="font-medium text-ink-2">
                        {rule.threshold} {METRIC_UNITS[rule.metric] || ''}
                      </span>
                      {Number(rule.window_s) > 0 ? ` for ${rule.window_s}s` : ''}
                      {rule.fleet_name ? ` · ${rule.fleet_name}` : ' · whole organisation'}
                    </p>
                  </div>

                  <Badge
                    tone={
                      { CRITICAL: 'critical', WARNING: 'warning', INFO: 'neutral' }[rule.severity] ||
                      'neutral'
                    }
                    size="sm"
                    icon={null}
                  >
                    {String(rule.severity).toLowerCase()}
                  </Badge>

                  {can('alert:rules') ? (
                    <button
                      type="button"
                      disabled={busyId === rule.id}
                      onClick={() => toggle(rule)}
                      className={`rounded-md px-2 py-1 text-[12.5px] font-medium disabled:opacity-50 ${
                        rule.enabled
                          ? 'text-ink-3 hover:bg-surface-2'
                          : 'text-brand hover:bg-brand-soft'
                      }`}
                    >
                      {busyId === rule.id ? '…' : rule.enabled ? 'Pause' : 'Enable'}
                    </button>
                  ) : (
                    <Badge tone={rule.enabled ? 'good' : 'neutral'} size="sm" icon={null}>
                      {rule.enabled ? 'live' : 'paused'}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </AsyncBoundary>
      </Card>

      <CreateRuleModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          rules.refetch();
        }}
      />
    </>
  );
}

function CreateRuleModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const org = useOrgField();
  const { scope } = useAuth();

  const empty = {
    name: '', metric: 'speed', operator: '>', threshold: '',
    windowSeconds: '0', severity: 'WARNING', fleetId: '', orgId: '',
  };
  const [form, setForm] = useState(empty);

  const metrics = useResource(open ? '/api/alerts/metrics' : null);
  const fleets = useResource(open ? '/api/fleets' : null);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useMutation(async () => {
    await api.post('/api/alerts/rules', {
      name: form.name.trim(),
      metric: form.metric,
      operator: form.operator,
      threshold: Number(form.threshold),
      windowSeconds: Number(form.windowSeconds) || 0,
      severity: form.severity,
      fleetId: form.fleetId || undefined,
      orgId: org.visible ? form.orgId : undefined,
    });
  });

  const submit = async () => {
    try {
      await create.run();
      toast.success('Rule created. It applies to the next packet that arrives.');
      setForm(empty);
      onCreated();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // A manager may only write a rule bound to one of their own fleets; the
  // server insists on it, so the field is required rather than optional here.
  const fleetRequired = Boolean(scope?.fleets);
  const availableFleets = (fleets.data?.fleets || []).filter(
    (fleet) => !org.visible || !form.orgId || fleet.org_id === form.orgId
  );

  const thresholdValid = form.threshold !== '' && Number.isFinite(Number(form.threshold));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New alert rule"
      description="Evaluated against every packet as it arrives."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.pending}
            onClick={submit}
            disabled={
              !form.name.trim() ||
              !thresholdValid ||
              (org.visible && !form.orgId) ||
              (fleetRequired && !form.fleetId)
            }
          >
            Create rule
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Rule name" required hint="What an operator will see when it fires.">
          <Input
            value={form.name}
            onChange={set('name')}
            placeholder="Reefer above -15 °C"
            autoFocus
          />
        </Field>

        {org.visible && (
          <OrgField
            value={form.orgId}
            onChange={(value) => setForm((c) => ({ ...c, orgId: value, fleetId: '' }))}
            organizations={org.organizations}
            loading={org.loading}
          />
        )}

        <div className="grid grid-cols-3 gap-3">
          <Field label="When" className="col-span-1">
            <Select
              value={form.metric}
              onChange={set('metric')}
              options={(metrics.data?.metrics || Object.keys(METRIC_UNITS)).map((m) => ({
                value: m,
                label: metricLabel(m),
              }))}
            />
          </Field>
          <Field label="Condition">
            <Select value={form.operator} onChange={set('operator')} options={OPERATORS} />
          </Field>
          <Field
            label="Threshold"
            required
            hint={METRIC_UNITS[form.metric] || undefined}
            error={form.threshold !== '' && !thresholdValid ? 'Must be a number.' : null}
          >
            <Input value={form.threshold} onChange={set('threshold')} inputMode="decimal" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Sustained for"
            hint="Seconds. Zero fires on the first reading — useful for tamper, noisy for speed."
          >
            <Input
              type="number"
              min="0"
              value={form.windowSeconds}
              onChange={set('windowSeconds')}
            />
          </Field>
          <Field label="Severity">
            <Select
              value={form.severity}
              onChange={set('severity')}
              options={[
                { value: 'INFO', label: 'Info' },
                { value: 'WARNING', label: 'Warning' },
                { value: 'CRITICAL', label: 'Critical' },
              ]}
            />
          </Field>
        </div>

        <Field
          label="Fleet"
          required={fleetRequired}
          hint={
            fleetRequired
              ? 'You can write rules for the fleets you manage.'
              : 'Leave empty to apply it to every vehicle in the organisation.'
          }
        >
          <Select
            value={form.fleetId}
            onChange={set('fleetId')}
            placeholder={fleetRequired ? 'Choose a fleet' : 'Whole organisation'}
            options={availableFleets.map((fleet) => ({ value: fleet.id, label: fleet.name }))}
          />
        </Field>
      </div>
    </Modal>
  );
}
